import {StyleSheet, ViewStyle, View} from 'react-native';
import React, {useState, useEffect, useMemo} from 'react';
import {Button, Modal, TextInput} from 'ui-kits';
import {Formik, FormikProps} from 'formik';
import {spacing} from 'theme/Units';
import {
  ForgetPasswordEmailValidation,
  ForgetPassWordEmailValidationAfterLogin,
  OTPValidation,
} from 'utils/schema';
import {resetOTPTimer} from 'constants/DefaultValues';
import {useDispatch, useSelector} from 'react-redux';
import {otpAttemps, otpTimer, getAuthData} from 'redux/selectors/authSelector';
import {resendOTPRequest} from 'redux/slices';
import {OtpForm, initialOTPValueType} from './OTPForm';

type propType = Readonly<{
  open: boolean;
  otpSent: boolean;
  onClose: () => void;
  onSubmit: (value: string) => void;
  onOTPSubmit: (otp: string, email?: string) => void;
  style?: ViewStyle;
}>;

type initialValueType = {
  email: string;
};
const initialValue: initialValueType = {
  email: '',
};

const initialOTPValue: initialOTPValueType = {
  otp: '',
};

export function ForgetPasswordModal(props: propType) {
  const {
    onClose,
    open,
    style: overrideStyle,
    onSubmit,
    onOTPSubmit,
    otpSent,
  } = props;
  const [emailState, setEmailState] = useState<string>('');
  const otpLastUpdate = useSelector(otpTimer);
  const attemptsLeft = useSelector(otpAttemps);
  const dispatch = useDispatch();
  const authData = useSelector(getAuthData);
  const authEmail = authData?.email || '';
  const [remainingTime, setRemainingTime] = useState<number>(0);
  useEffect(() => {
    const updateTimer = () => {
      if (otpLastUpdate) {
        const timeElapsed = Math.floor(
          (Date.now() - new Date(otpLastUpdate)) / 1000,
        );
        const newRemainingTime = Math.max(resetOTPTimer - timeElapsed, 0);
        setRemainingTime(newRemainingTime);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [otpLastUpdate]);

  const canResendOTP = useMemo(
    () => Boolean(remainingTime <= 0),
    [remainingTime],
  );

  function handleResendOTP() {
    dispatch(resendOTPRequest({email:emailState}));
  }

  return (
    <Modal style={overrideStyle} open={open} onClose={onClose}>
      {!otpSent ? (
        <Formik
          key="email-form"
          initialValues={initialValue}
          onSubmit={({email}) => {
            setEmailState(email);
            onSubmit(email);
          }}
          validationSchema={
            authEmail
              ? ForgetPassWordEmailValidationAfterLogin(authEmail)
              : ForgetPasswordEmailValidation
          }>
          {({
            dirty,
            errors,
            isValid,
            handleSubmit,
            handleChange,
            handleBlur,
            values,
          }: FormikProps<initialValueType>) => {
            return (
              <View style={styles.form}>
                <TextInput
                  style={styles.input}
                  label={'Email ID'}
                  required
                  value={values.email}
                  error={errors.email}
                  placeholder="Enter registered email ID"
                  onChangeText={handleChange('email')}
                  onBlur={handleBlur('email')}
                />
                <Button
                  disabled={!isValid || !dirty}
                  style={styles.button}
                  text="Submit"
                  onPress={handleSubmit}
                />
              </View>
            );
          }}
        </Formik>
      ) : (
        <Formik
          key="otp-form"
          initialValues={initialOTPValue}
          validationSchema={OTPValidation}
          onSubmit={({otp}) => onOTPSubmit(otp, email)}>
          {(formikProps: FormikProps<initialOTPValueType>) => {
            return (
              <OtpForm
                {...formikProps}
                heading="Check your mail!"
                label="Please enter the 6 digit OTP sent to your mail."
                buttonText="Verify and Proceed"
                canResendOTP={canResendOTP}
                attemptsLeft={attemptsLeft}
                remainingTime={remainingTime}
                handleResendOTP={handleResendOTP}
              />
            );
          }}
        </Formik>
      )}
    </Modal>
  );
}

const styles = StyleSheet.create({
  input: {
    width: 300,
  },
  form: {
    gap: spacing['4'],
    alignItems: 'center',
    padding: spacing['8'],
  },
  button: {
    width: '80%',
  },

  mailHeader: {
    fontWeight: 600,
  },
});
